import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MenuItem, TextField } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  parseAdditionalData,
  getSlotsForTeacher,
  mergeSlot,
  addSlotForTeacher,
  removeSlotForTeacher,
  WEEKDAY_OPTIONS
} from 'modules/newStudents/utils/additionalData'
import { formatTimeInput } from 'utils/date'
import { updateStudent } from 'services/StudentsService'

const DEBOUNCE_MS = 800

const INPUT_MIN_WIDTH = 72
const TIME_INPUT_WIDTH = 56

const SlotsEditorInline = ({ student, teacher, schoolSubjects, actionCallback, mode = 'both' }) => {
  const { subjects } = parseAdditionalData(student)
  const slots = getSlotsForTeacher(teacher, subjects, schoolSubjects)
  const debounceRef = useRef({})
  const [localTimes, setLocalTimes] = useState({})
  const latestRef = useRef({ student, subjects, schoolSubjects, actionCallback })
  const localTimesRef = useRef({})

  latestRef.current = { student, subjects, schoolSubjects, actionCallback }

  useEffect(() => {
    const next = {}
    slots.forEach((slot, i) => {
      const key = `${teacher.entryId || teacher.id}-${i}`
      const hasPendingSave = debounceRef.current[key]
      next[key] = hasPendingSave ? (localTimesRef.current[key] ?? slot.time ?? '') : (slot.time ?? '')
    })
    localTimesRef.current = next
    setLocalTimes(next)
  }, [student.id, student.additionalData, teacher.id, teacher.entryId, schoolSubjects])

  const saveAdditionalData = useCallback((merged) => {
    const { student: s, actionCallback: cb } = latestRef.current
    updateStudent({
      student: { ...s, additionalData: JSON.stringify(merged) },
      callback: () => typeof cb === 'function' && cb()
    })
  }, [])

  const handleDayChange = useCallback((slotIndex, day) => {
    const { subjects: subj, schoolSubjects: ss } = latestRef.current
    const slotsList = getSlotsForTeacher(teacher, subj, ss)
    const slot = slotsList[slotIndex] || { day: '', time: '' }
    const merged = mergeSlot({ subjects: subj }, teacher, slotIndex, day, slot.time, ss)
    saveAdditionalData(merged)
  }, [teacher, saveAdditionalData])

  const flushTimeSave = useCallback((slotIndex, time) => {
    const key = `${teacher.entryId || teacher.id}-${slotIndex}`
    const tRef = debounceRef.current[key]
    if (tRef) {
      clearTimeout(tRef)
      debounceRef.current[key] = null
    }
    const { subjects: subj, schoolSubjects: ss } = latestRef.current
    const slotsList = getSlotsForTeacher(teacher, subj, ss)
    const slot = slotsList[slotIndex] || { day: '', time: '' }
    const merged = mergeSlot({ subjects: subj }, teacher, slotIndex, slot.day, time, ss)
    saveAdditionalData(merged)
  }, [teacher, saveAdditionalData])

  const handleTimeChange = useCallback((slotIndex, time) => {
    const key = `${teacher.entryId || teacher.id}-${slotIndex}`
    localTimesRef.current[key] = time
    setLocalTimes(prev => ({ ...prev, [key]: time }))
    const tRef = debounceRef.current[key]
    if (tRef) clearTimeout(tRef)
    debounceRef.current[key] = setTimeout(() => flushTimeSave(slotIndex, time), DEBOUNCE_MS)
  }, [flushTimeSave])

  const handleTimeBlur = useCallback((slotIndex) => {
    const { subjects: subj, schoolSubjects: ss } = latestRef.current
    const slotsList = getSlotsForTeacher(teacher, subj, ss)
    const slot = slotsList[slotIndex] || { day: '', time: '' }
    const time = localTimesRef.current[`${teacher.entryId || teacher.id}-${slotIndex}`] ?? slot.time ?? ''
    flushTimeSave(slotIndex, time)
  }, [teacher, flushTimeSave])

  const handleAddSlot = useCallback(() => {
    const { subjects: subj, schoolSubjects: ss } = latestRef.current
    const merged = addSlotForTeacher({ subjects: subj }, teacher, ss)
    saveAdditionalData(merged)
  }, [teacher, saveAdditionalData])

  const handleRemoveSlot = useCallback((slotIndex) => {
    const { subjects: subj, schoolSubjects: ss } = latestRef.current
    const merged = removeSlotForTeacher({ subjects: subj }, teacher, slotIndex, ss)
    saveAdditionalData(merged)
  }, [teacher, saveAdditionalData])

  useEffect(() => () => {
    Object.values(debounceRef.current).forEach(t => t && clearTimeout(t))
  }, [])

  const showDay = mode === 'day' || mode === 'both'
  const showTime = mode === 'time' || mode === 'both'

  return (
    <SlotsRow onClick={e => e.stopPropagation()}>
      {slots.map((slot, i) => (
        <SlotPair key={`${teacher.entryId || teacher.id}-${i}`}>
          {showDay && (
            <DaySelect
              select
              size='small'
              value={slot.day === '' ? '' : Number(slot.day)}
              onChange={e => handleDayChange(i, e.target.value)}
              variant='outlined'
              sx={{ minWidth: INPUT_MIN_WIDTH, '& .MuiInputBase-input': { py: 0.4, fontSize: 11 } }}
            >
              {WEEKDAY_OPTIONS.map((opt, j) => (
                <MenuItem key={j} value={opt.value} sx={{ fontSize: 11 }}>{opt.label}</MenuItem>
              ))}
            </DaySelect>
          )}
          {showTime && (
            <TimeInput
              size='small'
              placeholder='16:00'
              value={localTimes[`${teacher.entryId || teacher.id}-${i}`] ?? slot.time ?? ''}
              onChange={e => {
                const prev = localTimes[`${teacher.entryId || teacher.id}-${i}`] ?? slot.time ?? ''
                handleTimeChange(i, formatTimeInput(e.target.value, prev))
              }}
              onBlur={() => handleTimeBlur(i)}
              variant='outlined'
              sx={{ width: TIME_INPUT_WIDTH, minWidth: TIME_INPUT_WIDTH, '& .MuiInputBase-input': { py: 0.4, fontSize: 11 } }}
            />
          )}
          {(mode === 'both' || mode === 'day') && slots.length > 1 && (
            <RemoveBtn type='button' onClick={e => { e.stopPropagation(); handleRemoveSlot(i) }} title='Usuń'>
              <RemoveIcon sx={{ fontSize: 16 }} />
            </RemoveBtn>
          )}
        </SlotPair>
      ))}
      {showDay && (
        <AddBtn type='button' onClick={e => { e.stopPropagation(); handleAddSlot() }} title='Dodaj termin'>
          <AddIcon sx={{ fontSize: 16 }} />
        </AddBtn>
      )}
    </SlotsRow>
  )
}

export default SlotsEditorInline

const SlotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const SlotPair = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DaySelect = styled(TextField)`
  .MuiInputBase-root { background: #fff; min-height: 28px; }
`

const TimeInput = styled(TextField)`
  .MuiInputBase-root { background: #fff; min-height: 28px; }
`

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: ${({ theme }) => theme.colors.darkGrey};
  display: flex;

  &:hover { color: ${({ theme }) => theme.colors.error}; }
`

const AddBtn = styled.button`
  background: none;
  border: 1px dashed ${({ theme }) => theme.colors.border || '#ccc'};
  cursor: pointer;
  padding: 2px 4px;
  color: ${({ theme }) => theme.colors.darkGrey};
  display: flex;
  border-radius: 4px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary.main};
    color: ${({ theme }) => theme.colors.secondary.main};
  }
`
